import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useEventCallback,
} from '@mui/material';
import { PmiDatePicker } from 'shell/components/shared/date-picker';
import { useCallback, useMemo, memo } from 'react';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { DateTime } from 'luxon';
import { defaultChoicePickerState, ETA_VIEW } from './constants';
import PmiTextField from 'shell/components/shared/text-field';

export type ChooseDateSelectedValue = { value: DateTime | null; choice: Array<DatePickerView> };

interface ChooseDateProps {
  selected?: DateTime | null;
  choice?: Array<DatePickerView>;
  required?: boolean;
  onChange: (value: DateTime | null, choice?: Array<DatePickerView>, age?: string) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  helperText?: string;
  withAge?: boolean;
  age?: string;
}

const ChooseDateComponent = ({
  selected = null,
  choice = defaultChoicePickerState,
  label = 'Data di nascita',
  onChange,
  required,
  disabled = false,
  error = false,
  helperText = '',
  withAge = false,
  age = '',
}: ChooseDateProps): React.ReactElement => {
  const views = useMemo(() => {
    return choice?.join(',').toString();
  }, [choice]);

  const isEta = useMemo(() => {
    return Boolean(choice?.includes(ETA_VIEW));
  }, [choice]);

  const onChangeRadio = useCallback(
    (event, value) => {
      const selectedValue = value.split(',') as Array<DatePickerView>;

      // Cambiando radio azzero l'altro valore: niente data se passo a "Età", niente età altrimenti.
      const isEtaSelected = selectedValue.includes(ETA_VIEW);

      onChange(isEtaSelected ? null : selected || null, selectedValue, undefined);
    },
    [onChange, selected]
  );

  const onChangePicker = useEventCallback((date: DateTime | null) => {
    onChange(date, choice);
  });

  const onChangeAge = useEventCallback((value: string) => {
    onChange(null, choice, value);
  });

  const hasRadio =
    (choice.includes('month') && choice.includes('year') && !choice.includes('day')) ||
    (!choice.includes('month') && choice.includes('year') && !choice.includes('day'));

  return (
    <Grid item xs={12} container columnSpacing={{ xs: 0, md: 1 }}>
      {withAge && isEta ? (
        <Grid item xs={12} md={4}>
          <PmiTextField
            label={required ? 'Età (anni) *' : 'Età (anni)'}
            aria-label="Età (anni)"
            id="choose-date-age"
            fullWidth
            type="number"
            value={age || ''}
            disabled={disabled}
            error={error}
            helperText={helperText}
            onChange={(event) => onChangeAge(event.target.value)}
          />
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={disabled ? (hasRadio ? 8 : 12) : 4}>
        <PmiDatePicker
          label={required ? `${label} *` : label}
          value={selected}
          views={choice}
          disabled={disabled || isEta}
          onChange={onChangePicker}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="choose-date-picker"
              fullWidth
              error={error}
              helperText={helperText}
            />
          )}
        />
      </Grid>
      {disabled ? (
        <Grid item xs={12} md={4} display="flex">
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Date Picker selezione"
              id="viewsDatePicker"
              value={views}
              onChange={onChangeRadio}
            >
              {choice.includes('month') && choice.includes('year') && !choice.includes('day') ? (
                <FormControlLabel
                  value="month,year"
                  disabled={disabled}
                  control={<Radio />}
                  label="Solo Mese e anno"
                />
              ) : undefined}
              {!choice.includes('month') && choice.includes('year') && !choice.includes('day') ? (
                <FormControlLabel
                  value="year"
                  disabled={disabled}
                  control={<Radio />}
                  label="Solo Anno"
                />
              ) : undefined}
            </RadioGroup>
          </FormControl>
        </Grid>
      ) : (
        <Grid item xs={12} md={8}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Date Picker selezione"
              id="viewsDatePicker"
              value={views}
              onChange={onChangeRadio}
            >
              <FormControlLabel
                value="day,month,year"
                disabled={disabled}
                style={{
                  marginLeft: '0',
                }}
                control={<Radio />}
                label="Completa"
              />
              <FormControlLabel
                value="month,year"
                disabled={disabled}
                style={{
                  marginLeft: '0',
                }}
                control={<Radio />}
                label="Solo mese e anno"
              />
              <FormControlLabel
                value="year"
                style={{
                  marginLeft: '0',
                }}
                disabled={disabled}
                control={<Radio />}
                label="Solo anno"
              />
              <FormControlLabel
                value="eta"
                style={{
                  marginLeft: '0',
                }}
                disabled={disabled}
                control={<Radio />}
                label="Età"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

ChooseDateComponent.displayName = 'Choose Date Person Filter component';

const ChooseDate = memo(ChooseDateComponent) as typeof ChooseDateComponent;

export default ChooseDate;
